import { Box, Collapse } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReactElement, ReactNode, useState } from 'react';
import { tokens } from '@synergy/theme-palo';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import TuneIcon from '@mui/icons-material/Tune';

import { DocumentStreamKind } from '../../../common/models/document-stream-entry';
import { PoolTimelineChangesPresenter } from '../../../common/components/timeline/components/pool-timeline-changes-presenter';
import { PoolTimelineItem } from '../../../common/components/timeline/models/pool-timeline-item';
import { PoolTimelineItemComment } from '../../../common/components/timeline/models/pool-timeline-itemComment';
import { StreamReply } from './stream-reply';
import { formatStreamTime } from './stream-time';

const c = tokens.color;

/**
 * The document stream as rows (#2154).
 *
 * Replaces the timeline the stream borrowed from the old activity panel. That
 * layout grouped entries under day headers and put the kind of an entry
 * nowhere: with four sources in one list, a reader could no longer tell a mail
 * from an edit without reading both lines. Christian's study answers that with
 * one dense row per entry — who, what kind, when, what happened — which is what
 * this draws.
 *
 * The day headers are gone on purpose; the time now sits in the row itself and
 * reads as "heute 09:14" for the dates a reader thinks about in words.
 */

interface KindLook {
    labelId: string;
    ink: string;
    soft: string;
    line: string;
    icon: ReactNode;
}

const KIND_LOOK: Record<DocumentStreamKind, KindLook> = {
    [DocumentStreamKind.Status]: {
        labelId: 'app.stream.filter.status',
        ink: c.accentHi,
        soft: c.accentSoft,
        line: c.accentLine,
        icon: <FlagOutlinedIcon />,
    },
    [DocumentStreamKind.Internal]: {
        labelId: 'app.stream.filter.internal',
        ink: c.internal,
        soft: c.internalSoft,
        line: c.internalLine,
        icon: <ChatBubbleOutlineIcon />,
    },
    [DocumentStreamKind.Message]: {
        labelId: 'app.stream.filter.message',
        ink: c.warnInk,
        soft: c.warnSoft,
        line: c.warnLine,
        icon: <MailOutlineIcon />,
    },
    [DocumentStreamKind.System]: {
        // Not "System": the badge stands beside the author, and a change a
        // person made is not the system's doing (R-24). The filter keeps its
        // own wording — there it names a group of entries, not one of them.
        labelId: 'app.stream.badge.change',
        ink: c.ink2,
        soft: c.neutralSoft,
        line: c.lineHard,
        icon: <TuneIcon />,
    },
};

const FALLBACK: KindLook = KIND_LOOK[DocumentStreamKind.System];

export interface PaloStreamListProps {
    items: PoolTimelineItem[];
    onCommentUpdate?: (entryId: number, commentId: number, text: string) => Promise<void>;
    onCommentDelete?: (entryId: number, commentId: number) => Promise<void>;
}

interface StreamRowCommentProps {
    comment: PoolTimelineItemComment;
    onUpdate?: (text: string) => Promise<void>;
    onDelete?: () => Promise<void>;
}

/** A reply under its entry, quoted the way the study draws it. */
const StreamRowComment = ({ comment, onUpdate, onDelete }: StreamRowCommentProps): ReactElement => {
    const [editing, setEditing] = useState(false);

    return (
        <Box
            sx={{
                marginTop: '6px',
                padding: '6px 9px',
                borderLeft: `2px solid ${c.internalLine}`,
                backgroundColor: c.surfaceAlt,
                fontSize: '12px',
                lineHeight: '17px',
                color: c.ink,
            }}
        >
            {editing && onUpdate ? (
                <StreamReply
                    initialText={comment.text}
                    autoFocus={true}
                    submitId="app.common.save"
                    onSubmit={async text => {
                        await onUpdate(text);
                        setEditing(false);
                    }}
                    onCancel={() => setEditing(false)}
                />
            ) : (
                <Box sx={{ whiteSpace: 'pre-wrap' }}>{comment.text}</Box>
            )}
            <Box sx={{ display: 'flex', gap: '9px', fontSize: '10.5px', color: c.ink3, paddingTop: '2px' }}>
                <Box component="span">{comment.footer}</Box>
                {comment.editable && !editing && onUpdate && (
                    <Box
                        component="span"
                        role="button"
                        onClick={() => setEditing(true)}
                        sx={{ color: c.accent, cursor: 'pointer' }}
                    >
                        <FormattedMessage id="app.common.edit" />
                    </Box>
                )}
                {comment.editable && !editing && onDelete && (
                    <Box component="span" role="button" onClick={onDelete} sx={{ color: c.crit, cursor: 'pointer' }}>
                        <FormattedMessage id="app.common.delete" />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

type StreamRowProps = Omit<PaloStreamListProps, 'items'> & { item: PoolTimelineItem };

const StreamRow = ({ item, onCommentUpdate, onCommentDelete }: StreamRowProps): ReactElement => {
    const intl = useIntl();
    const [detailsOpen, setDetailsOpen] = useState(false);
    const look = item.kind ? KIND_LOOK[item.kind] : FALLBACK;
    const hasDetails = item.changes.length > 0;

    return (
        <Box
            sx={{
                display: 'flex',
                gap: '11px',
                padding: '10px 13px',
                borderBottom: `1px solid ${c.lineSoft}`,
                '&:last-of-type': { borderBottom: 0 },
                '&:hover': { backgroundColor: c.surface2 },
            }}
        >
            <Box
                sx={{
                    width: 26,
                    height: 26,
                    flexShrink: 0,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    border: `1.5px solid ${look.line}`,
                    backgroundColor: look.soft,
                    color: look.ink,
                    '& svg': { width: 13, height: 13 },
                }}
            >
                {look.icon}
            </Box>

            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                {/* Wraps rather than overflows: docked at the right the entry has
                    a column, not the width of the window, and a line that runs
                    past the edge takes the start of every entry with it (#2184). */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', rowGap: '3px' }}>
                    {item.actor && (
                        <Box
                            component="span"
                            sx={{
                                fontSize: '12px',
                                fontWeight: 620,
                                color: c.ink,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {item.actor}
                        </Box>
                    )}
                    {item.origin && (
                        <Box
                            component="span"
                            sx={{
                                fontSize: '10px',
                                fontWeight: 600,
                                letterSpacing: '.04em',
                                padding: '1px 6px',
                                borderRadius: '8px',
                                border: `1px solid ${c.lineHard}`,
                                color: c.ink2,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {item.origin}
                        </Box>
                    )}
                    {item.kind && (
                        <Box
                            component="span"
                            sx={{
                                fontSize: '10px',
                                fontWeight: 600,
                                letterSpacing: '.04em',
                                textTransform: 'uppercase',
                                padding: '1px 6px',
                                borderRadius: '8px',
                                backgroundColor: look.soft,
                                color: look.ink,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <FormattedMessage id={look.labelId} />
                        </Box>
                    )}
                    <Box
                        component="span"
                        sx={{
                            marginLeft: 'auto',
                            fontSize: '11.5px',
                            fontVariantNumeric: 'tabular-nums',
                            color: c.ink3,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {formatStreamTime(item.timestamp, intl)}
                    </Box>
                </Box>

                {!item.bodyOnly && (
                    <Box sx={{ fontSize: '12.5px', lineHeight: '18px', color: c.ink, paddingTop: '2px' }}>
                        {item.sentence ?? item.header}
                    </Box>
                )}

                {item.content && (
                    <Box sx={{ fontSize: '12.5px', lineHeight: '18px', paddingTop: '2px' }}>{item.content}</Box>
                )}

                {item.details && <Box sx={{ fontSize: '11.5px', paddingTop: '2px' }}>{item.details}</Box>}

                {hasDetails && (
                    <>
                        <Box
                            component="span"
                            role="button"
                            onClick={() => setDetailsOpen(open => !open)}
                            sx={{
                                display: 'inline-block',
                                fontSize: '10.5px',
                                color: c.accent,
                                cursor: 'pointer',
                                paddingTop: '3px',
                            }}
                        >
                            <FormattedMessage
                                id={detailsOpen ? 'app.stream.details.hide' : 'app.stream.details.show'}
                                values={{ count: item.changes.length }}
                            />
                        </Box>
                        <Collapse in={detailsOpen} unmountOnExit={true}>
                            <Box sx={{ paddingTop: '6px' }}>
                                <PoolTimelineChangesPresenter changes={item.changes} />
                            </Box>
                        </Collapse>
                    </>
                )}

                {item.comments.map(comment => (
                    <StreamRowComment
                        key={comment.id}
                        comment={comment}
                        onUpdate={onCommentUpdate ? text => onCommentUpdate(item.id, comment.id, text) : undefined}
                        onDelete={onCommentDelete ? () => onCommentDelete(item.id, comment.id) : undefined}
                    />
                ))}
            </Box>
        </Box>
    );
};

export const PaloStreamList = ({ items, onCommentUpdate, onCommentDelete }: PaloStreamListProps): ReactElement => (
    <Box>
        {items.map(item => (
            <StreamRow
                key={item.key ?? item.id}
                item={item}
                onCommentUpdate={onCommentUpdate}
                onCommentDelete={onCommentDelete}
            />
        ))}
    </Box>
);
